import {
  MAX_QUERY_LIMIT,
  type AIAction,
  type AIExplanationTopic,
  type FilterOperator,
  type TimeGrain,
  type VisualizationType,
} from '@aibi/contracts';
import type { AIContext, AIContextField } from '../context/ai-context.js';
import {
  actionError,
  type AIActionError,
  type AICandidate,
  type AIClarification,
} from '../errors.js';
import { shapeFromContext, suggestVisualization } from '../suggest/suggest-visualization.js';
import { createFieldMatcher } from './field-matcher.js';
import { comparable, normalizeText } from './normalize.js';

/** Resultado del interprete: acciones, una pregunta de aclaracion o un error. */
export type IntentResult =
  | { readonly kind: 'actions'; readonly actions: readonly AIAction[]; readonly rationale: string }
  | { readonly kind: 'clarification'; readonly clarification: AIClarification }
  | { readonly kind: 'error'; readonly error: AIActionError };

const GRAIN_WORDS: Readonly<Record<string, TimeGrain>> = {
  hora: 'hour',
  dia: 'day',
  diario: 'day',
  semana: 'week',
  semanal: 'week',
  mes: 'month',
  mensual: 'month',
  trimestre: 'quarter',
  trimestral: 'quarter',
  ano: 'year',
  anio: 'year',
  anual: 'year',
};

/** Granularidad nombrada por una frase normalizada ("mes", "meses", "anual"...). */
export const grainOf = (phrase: string): TimeGrain | undefined =>
  GRAIN_WORDS[phrase] ?? GRAIN_WORDS[phrase.replace(/s$/, '')];

const VISUALIZATION_WORDS: Readonly<Record<string, VisualizationType>> = {
  barra: 'bar',
  barras: 'bar',
  columna: 'column',
  columnas: 'column',
  linea: 'line',
  lineas: 'line',
  tendencia: 'line',
  area: 'area',
  areas: 'area',
  tabla: 'table',
  kpi: 'kpi',
  indicador: 'kpi',
  tarjeta: 'kpi',
  pastel: 'pie',
  circular: 'pie',
  dona: 'donut',
  anillo: 'donut',
  mapa: 'map',
  dispersion: 'scatter',
  calor: 'heatmap',
  treemap: 'treemap',
  cascada: 'waterfall',
};

const VIZ_VERB =
  'ponlo|ponla|pon|muestralo|muestrala|cambialo|cambiala|cambia|convierte|conviertelo|conviertela|usa|usar|visualiza|visualizalo|graficalo|grafica';
const VIZ_CHART = 'grafico|grafica|diagrama|visualizacion|tipo';
const VIZ_WORD = `mapa de calor|${Object.keys(VISUALIZATION_WORDS).join('|')}`;

/**
 * Un tipo de visualizacion solo se reconoce con una senal explicita ("en barras", "ponlo
 * como tabla", "grafico de lineas") o al inicio de la frase ("kpi de ventas"), para no
 * confundir palabras como "area" o "linea" con nombres de campos.
 */
const VISUALIZATION_CLAUSES: readonly RegExp[] = [
  new RegExp(
    String.raw`\b(?:(?:(?:${VIZ_VERB})\s+(?:(?:en|a|como)\s+)?|(?:en|como)\s+)(?:(?:un|una)\s+)?(?:(?:${VIZ_CHART})\s+(?:de\s+)?)?|(?:${VIZ_CHART})\s+(?:de\s+)?)(${VIZ_WORD})\b`,
  ),
  new RegExp(
    String.raw`^\s*(?:(?:muestrame|muestra|quiero|dame|ver|crea|crear|haz|un|una)\s+)*(${VIZ_WORD})\b(?:\s+de\b)?`,
  ),
];

const LEADING_STOPWORDS = new Set([
  'muestrame',
  'muestra',
  'mostrar',
  'muestre',
  'ver',
  'veo',
  'quiero',
  'quisiera',
  'dame',
  'deme',
  'necesito',
  'analiza',
  'analizar',
  'compara',
  'comparar',
  'agrega',
  'agregar',
  'anade',
  'anadir',
  'incluye',
  'incluir',
  'ponme',
  'tambien',
  'ahora',
  'cuales',
  'son',
  'cual',
  'es',
  'cuanto',
  'cuantos',
  'cuantas',
  'y',
  'e',
  'las',
  'los',
  'el',
  'la',
  'lo',
  'de',
  'del',
  'un',
  'una',
  'unos',
  'unas',
  'me',
  'mi',
  'mis',
  'al',
  'a',
  'en',
  'por',
  'con',
  'que',
  'total',
]);
const TRAILING_STOPWORDS = new Set(['por', 'favor', 'porfa', 'gracias', 'y', 'de', 'en', 'con']);

const trimPhrase = (phrase: string): string => {
  const tokens = normalizeText(phrase).split(' ').filter(Boolean);
  while (tokens.length > 0 && LEADING_STOPWORDS.has(tokens[0] ?? '')) tokens.shift();
  while (tokens.length > 0 && TRAILING_STOPWORDS.has(tokens.at(-1) ?? '')) tokens.pop();
  return tokens.join(' ');
};

const splitList = (text: string): string[] =>
  text
    .split(/\s*,\s*|\s+y\s+|\s+e\s+|\s+con\s+/)
    .map(trimPhrase)
    .filter((phrase) => phrase !== '');

const candidateOf = (field: AIContextField): AICandidate => ({
  id: field.id,
  label: field.label,
  kind: field.kind,
});

class Stop extends Error {
  constructor(readonly result: Exclude<IntentResult, { kind: 'actions' }>) {
    super(result.kind);
  }
}

/** Acumula acciones con ids deterministas y describe lo que se propone. */
class ActionBuilder {
  readonly actions: AIAction[] = [];
  readonly notes: string[] = [];
  #filterSeq: number;

  constructor(existingFilterIds: readonly string[]) {
    let max = 0;
    for (const id of existingFilterIds) {
      const match = /^f(\d+)$/.exec(id);
      if (match?.[1] !== undefined) max = Math.max(max, Number(match[1]));
    }
    this.#filterSeq = max;
  }

  nextId(): string {
    return `a${String(this.actions.length + 1)}`;
  }

  nextFilterId(): string {
    this.#filterSeq += 1;
    return `f${String(this.#filterSeq)}`;
  }

  push(action: AIAction, note: string): void {
    this.actions.push(action);
    this.notes.push(note);
  }
}

/**
 * Interprete determinista de lenguaje natural (espanol) a AI Actions. Resuelve campos por
 * etiquetas, sinonimos, ids y diccionario del contexto; ante varias coincidencias pide
 * aclaracion y ante ninguna devuelve un error con candidatos. Nunca inventa campos ni
 * valores: los filtros solo usan miembros conocidos del contexto.
 *
 * @example
 * parseUserIntent('Muestrame las ventas por region', context);
 * // { kind: 'actions', actions: [addMeasure(total_sales), addDimension(dim.region)] }
 */
export function parseUserIntent(prompt: string, context: AIContext): IntentResult {
  try {
    return interpret(normalizeText(prompt), context);
  } catch (error) {
    if (error instanceof Stop) return error.result;
    throw error;
  }
}

function interpret(input: string, context: AIContext): IntentResult {
  const matcher = createFieldMatcher(context);
  const analysis = context.analysis;
  const builder = new ActionBuilder(analysis?.filters.map((filter) => filter.id) ?? []);
  const fail = (error: AIActionError): never => {
    throw new Stop({ kind: 'error', error });
  };
  const clarify = (clarification: AIClarification): never => {
    throw new Stop({ kind: 'clarification', clarification });
  };
  const done = (fallbackMessage?: string): IntentResult => {
    if (builder.actions.length === 0) {
      return {
        kind: 'error',
        error: actionError(
          fallbackMessage === undefined ? 'UNRECOGNIZED_INTENT' : 'STATE_INVALID',
          fallbackMessage ??
            'No entendi la instruccion. Pruebe, por ejemplo, "ventas por region", "filtra Mexico" o "ponlo en barras".',
          { type: 'unknown' },
        ),
      };
    }
    return { kind: 'actions', actions: builder.actions, rationale: builder.notes.join('; ') + '.' };
  };

  const inAnalysis = (fieldId: string): boolean =>
    analysis?.dimensions.some((d) => d.dimensionId === fieldId) === true ||
    analysis?.measures.some((m) => m.measureId === fieldId) === true;

  // 1. Reinicios
  if (
    /\b(?:limpia|limpiar|reinicia|reiniciar|resetea|resetear|borra todo|empieza de nuevo|nuevo analisis)\b/.test(
      input,
    )
  ) {
    if (/\bfiltros?\b/.test(input)) {
      builder.push(
        { id: builder.nextId(), type: 'clearFilters', payload: {} },
        'quitar todos los filtros',
      );
    } else if (/\bselecci(?:on|ones)\b/.test(input)) {
      builder.push(
        { id: builder.nextId(), type: 'resetSelections', payload: {} },
        'limpiar las selecciones',
      );
    } else {
      builder.push(
        { id: builder.nextId(), type: 'resetAnalysis', payload: {} },
        'reiniciar el analisis',
      );
    }
    return done();
  }

  // 2. Explicaciones
  if (
    /\b(?:por que|explica|explicame|como se calcula|como se calculo|como se obtiene|que filtros|que campos|de donde sale)\b/.test(
      input,
    )
  ) {
    let topic: AIExplanationTopic = 'result';
    let measureId: string | undefined;
    if (/\bfiltros?\b/.test(input)) topic = 'filters';
    else if (/\bcampos?\b/.test(input)) topic = 'fields';
    else if (/\bcalcul|\bobtiene\b/.test(input)) {
      topic = 'measure';
      const phrase = trimPhrase(input.replace(/^.*?\b(?:calcula|calculo|calculan|obtiene)\b/, ''));
      const outcome = phrase === '' ? null : matcher.match(phrase, 'measure');
      if (outcome?.status === 'matched') measureId = outcome.field.id;
      else if (outcome?.status === 'ambiguous') {
        clarify({
          question: 'Que medida desea que explique?',
          candidates: outcome.candidates.map(candidateOf),
          reason: 'Varias medidas coinciden con el termino.',
          affectedActionIds: [builder.nextId()],
          term: phrase,
        });
      } else if (analysis?.measures.length === 1) measureId = analysis.measures[0]?.measureId;
      if (measureId === undefined) topic = 'result';
    }
    builder.push(
      {
        id: builder.nextId(),
        type: 'requestExplanation',
        payload: { topic, ...(measureId === undefined ? {} : { measureId }) },
      },
      `explicar (${topic})`,
    );
    return done();
  }

  // 3. Sugerencias de visualizacion
  if (
    /\b(?:sugiere|sugiereme|sugerir|sugerencia|recomienda|recomiendame|que grafic\w*|cual grafic\w*|que visualizacion)\b/.test(
      input,
    )
  ) {
    const suggestion = suggestVisualization({
      shape: shapeFromContext(context),
      capabilities: context.visualizations,
      dimensionKeys: analysis?.dimensions.map((d) => d.key) ?? [],
      measureKeys: analysis?.measures.map((m) => m.key) ?? [],
      current: analysis?.visualization ?? null,
    });
    if (!suggestion || !analysis || analysis.measures.length + analysis.dimensions.length === 0) {
      return done('Agregue campos al analisis antes de pedir una sugerencia de visualizacion.');
    }
    builder.push(
      { id: builder.nextId(), type: 'suggestVisualization', payload: { type: suggestion.type } },
      `sugerir ${suggestion.label}`,
    );
    return done();
  }

  // 4. Quitar filtros
  const removeFilter =
    /\b(?:no quiero|quita|quitar|quitale|elimina|eliminar|borra|borrar|remueve|sin)\b(?:\s+(?:el|los|ese|este))?\s+filtros?\b(?:\s+(?:de|del|por|en|a|sobre))?(.*)$/.exec(
      input,
    );
  if (removeFilter) {
    const filters = analysis?.filters ?? [];
    const phrase = trimPhrase(removeFilter[1] ?? '');
    const filterLabel = (filter: (typeof filters)[number]): string => {
      const id =
        filter.target.kind === 'dimension' ? filter.target.dimensionId : filter.target.measureId;
      return `${matcher.field(id)?.label ?? id} (${filter.values.map(String).join(', ')})`;
    };
    let matches = filters;
    if (phrase !== '') {
      const target = comparable(phrase);
      const fieldOutcome = matcher.match(phrase);
      matches = filters.filter((filter) => {
        const id =
          filter.target.kind === 'dimension' ? filter.target.dimensionId : filter.target.measureId;
        return (
          filter.values.some((value) => comparable(String(value)) === target) ||
          (fieldOutcome.status === 'matched' && fieldOutcome.field.id === id)
        );
      });
    }
    const [only] = matches;
    if (only && matches.length === 1) {
      builder.push(
        { id: builder.nextId(), type: 'removeFilter', payload: { filterId: only.id } },
        `quitar el filtro ${filterLabel(only)}`,
      );
      return done();
    }
    if (matches.length > 1) {
      clarify({
        question: 'Que filtro desea quitar?',
        candidates: matches.map((filter) => ({
          id: filter.id,
          label: filterLabel(filter),
          kind: 'filter',
        })),
        reason: 'Varios filtros coinciden.',
        affectedActionIds: [builder.nextId()],
        ...(phrase === '' ? {} : { term: phrase }),
      });
    }
    return fail(
      actionError('FILTER_NOT_FOUND', 'No hay un filtro activo que coincida.', {
        type: 'removeFilter',
        ...(phrase === '' ? {} : { term: phrase }),
      }),
    );
  }

  // 5. Quitar campos
  const removeFields =
    /^(?:quita|quitar|quitale|elimina|eliminar|remueve|remover|saca|sacar)\b(.*)$/.exec(input);
  if (removeFields) {
    for (const phrase of splitList(removeFields[1] ?? '')) {
      const outcome = matcher.match(phrase);
      if (outcome.status === 'ambiguous') {
        const present = outcome.candidates.filter((field) => inAnalysis(field.id));
        const [single] = present;
        if (single && present.length === 1) {
          pushRemove(builder, single);
          continue;
        }
        clarify({
          question: `Que campo desea quitar ("${phrase}")?`,
          candidates: (present.length > 0 ? present : outcome.candidates).map(candidateOf),
          reason: 'Varios campos coinciden con el termino.',
          affectedActionIds: [builder.nextId()],
          term: phrase,
        });
      }
      if (outcome.status !== 'matched' || !inAnalysis(outcome.field.id)) {
        fail(
          actionError('FIELD_NOT_FOUND', `"${phrase}" no forma parte del analisis.`, {
            type: 'unknown',
            term: phrase,
          }),
        );
      }
      if (outcome.status === 'matched') pushRemove(builder, outcome.field);
    }
    return done();
  }

  // 6. Clausulas combinables
  let text = ` ${input} `;
  const take = (pattern: RegExp): RegExpExecArray | null => {
    const match = pattern.exec(text);
    if (match) text = `${text.slice(0, match.index)} ${text.slice(match.index + match[0].length)} `;
    return match;
  };

  let visualization: VisualizationType | null = null;
  let vizMatch: RegExpExecArray | null = null;
  for (const clause of VISUALIZATION_CLAUSES) {
    vizMatch ??= take(clause);
  }
  if (vizMatch) {
    const word = vizMatch[1] ?? '';
    visualization = word === 'mapa de calor' ? 'heatmap' : (VISUALIZATION_WORDS[word] ?? null);
    if (visualization && !context.visualizations.some((viz) => viz.type === visualization)) {
      fail(
        actionError(
          'ACTION_NOT_ALLOWED',
          'Ese tipo de visualizacion no esta disponible.',
          { type: 'setVisualization', term: word },
          context.visualizations.map((viz) => ({
            id: viz.type,
            label: viz.label,
            kind: 'visualization',
          })),
        ),
      );
    }
  }

  let topN: { limit: number; direction: 'asc' | 'desc' } | null = null;
  const top = take(
    /\b(?:top|los|las)?\s*(\d{1,7})\s+(mejores|principales|primeros|primeras|mayores|peores|menores|ultimos|ultimas)\b|\btop\s+(\d{1,7})\b/,
  );
  if (top) {
    const limit = Number(top[1] ?? top[3]);
    const worst = /peores|menores|ultim/.test(top[2] ?? '');
    if (limit >= 1 && limit <= MAX_QUERY_LIMIT) topN = { limit, direction: worst ? 'asc' : 'desc' };
  }
  const limitMatch = take(
    /\b(?:limita(?:r|lo|la)?(?:\s+a)?|solo|maximo)\s+(\d{1,7})\s*(?:filas|registros|resultados)?\b/,
  );
  let limit: number | null = null;
  if (limitMatch) {
    const value = Number(limitMatch[1]);
    if (value >= 1 && value <= MAX_QUERY_LIMIT) limit = value;
  }

  const sortMatch = take(
    /\b(?:ordena(?:r|do|da|dos|das|lo|la)?|orden)\b(?:\s+(?:por|segun|de))?\s+(.+?)(?:\s+(descendente|desc|ascendente|asc|de mayor a menor|de menor a mayor))?\s*$/,
  );

  let grouping: { grandTotal?: boolean; subtotals?: boolean } | null = null;
  if (take(/\bsin\s+(?:sub)?totales?\b/)) grouping = { grandTotal: false, subtotals: false };
  if (take(/\b(?:con\s+)?subtotales\b/)) grouping = { ...grouping, subtotals: true };
  if (take(/\bcon\s+totale?s?\b|\btotal\s+general\b/)) grouping = { ...grouping, grandTotal: true };

  const filterMatch = take(
    /\b(?:filtra(?:r|do|da|lo|la)?|filtro|solo|solamente|unicamente)\b((?:\s+(?:por|a|en|de|para|los|las|el|la))*)\s+(.+?)\s*$/,
  );

  // Campos: "<medidas> por <dimensiones> [por <dimensiones>]"
  const [measurePart = '', ...dimensionParts] = text.trim().split(/\s+por\s+/);
  const addedTemporal: { dimensionId: string; actionIndex: number }[] = [];
  const addedMeasures: string[] = [];

  const addField = (field: AIContextField, grain?: TimeGrain): void => {
    if (field.kind === 'measure') {
      if (!inAnalysis(field.id) && !addedMeasures.includes(field.id)) {
        builder.push(
          { id: builder.nextId(), type: 'addMeasure', payload: { measureId: field.id } },
          `agregar ${field.label}`,
        );
      }
      addedMeasures.push(field.id);
      return;
    }
    const existing = analysis?.dimensions.find((d) => d.dimensionId === field.id);
    const timeGrain = field.temporal ? (grain ?? field.defaultTimeGrain) : undefined;
    if (existing) {
      if (timeGrain === undefined || existing.timeGrain === timeGrain) return;
      builder.push(
        { id: builder.nextId(), type: 'removeDimension', payload: { dimensionId: field.id } },
        `cambiar la granularidad de ${field.label}`,
      );
    }
    if (
      builder.actions.some((a) => a.type === 'addDimension' && a.payload.dimensionId === field.id)
    )
      return;
    if (field.temporal)
      addedTemporal.push({ dimensionId: field.id, actionIndex: builder.actions.length });
    builder.push(
      {
        id: builder.nextId(),
        type: 'addDimension',
        payload: { dimensionId: field.id, ...(timeGrain === undefined ? {} : { timeGrain }) },
      },
      `agregar ${field.label}${timeGrain === undefined ? '' : ` por ${grainLabel(timeGrain)}`}`,
    );
  };

  const resolve = (phrase: string, preferred: AIContextField['kind']): AIContextField => {
    let outcome = matcher.match(phrase, preferred);
    if (outcome.status === 'none') outcome = matcher.match(phrase);
    if (outcome.status === 'matched') return outcome.field;
    if (outcome.status === 'ambiguous') {
      return clarify({
        question: `A que campo se refiere con "${phrase}"?`,
        candidates: outcome.candidates.map(candidateOf),
        reason: 'Varios campos del modelo coinciden con el termino.',
        affectedActionIds: [builder.nextId()],
        term: phrase,
      });
    }
    return fail(
      actionError(
        'FIELD_NOT_FOUND',
        `No encontre "${phrase}" en el modelo.`,
        { type: preferred === 'measure' ? 'addMeasure' : 'addDimension', term: phrase },
        outcome.suggestions.map(candidateOf),
      ),
    );
  };

  for (const phrase of splitList(measurePart)) addField(resolve(phrase, 'measure'));
  for (const part of dimensionParts) {
    for (const phrase of splitList(part)) {
      const grain = grainOf(phrase);
      if (grain === undefined) {
        addField(resolve(phrase, 'dimension'));
        continue;
      }
      applyGrain(grain, phrase);
    }
  }

  function applyGrain(grain: TimeGrain, phrase: string): void {
    const recent = addedTemporal.at(-1);
    if (recent) {
      const action = builder.actions[recent.actionIndex];
      if (action?.type === 'addDimension') {
        builder.actions[recent.actionIndex] = {
          ...action,
          payload: { ...action.payload, timeGrain: grain },
        };
        const field = matcher.field(recent.dimensionId);
        builder.notes[recent.actionIndex] =
          `agregar ${field?.label ?? recent.dimensionId} por ${grainLabel(grain)}`;
      }
      return;
    }
    const temporalInAnalysis = analysis?.dimensions.find(
      (d) => matcher.field(d.dimensionId)?.temporal === true,
    );
    const temporalField =
      (temporalInAnalysis && matcher.field(temporalInAnalysis.dimensionId)) ?? pickTemporal(phrase);
    addField(temporalField, grain);
  }

  function pickTemporal(grainPhrase: string): AIContextField {
    const temporal = context.fields.filter((field) => field.kind === 'dimension' && field.temporal);
    const primary = context.model.primaryDateDimensionId;
    const preferred =
      primary === undefined ? undefined : temporal.find((field) => field.id === primary);
    if (preferred) return preferred;
    const [only] = temporal;
    if (only && temporal.length === 1) return only;
    if (temporal.length > 1) {
      return clarify({
        question: 'Que fecha desea usar?',
        candidates: temporal.map(candidateOf),
        reason: 'El modelo tiene varias dimensiones de fecha.',
        affectedActionIds: [builder.nextId()],
        term: grainPhrase,
      });
    }
    return fail(
      actionError('FIELD_NOT_FOUND', 'El modelo no tiene una dimension de fecha.', {
        type: 'addDimension',
      }),
    );
  }

  if (filterMatch) addFilters(filterMatch[2] ?? '');

  function addFilters(phrase: string): void {
    const measureFilter =
      /^(.+?)\s+(mayor(?:es)?\s+(?:a|que)|menor(?:es)?\s+(?:a|que)|superior(?:es)?\s+a|inferior(?:es)?\s+a|igual(?:es)?\s+a)\s+(\d{1,15})$/.exec(
        phrase,
      );
    if (measureFilter) {
      const field = resolve(trimPhrase(measureFilter[1] ?? ''), 'measure');
      const word = measureFilter[2] ?? '';
      const operator: FilterOperator = /^(?:mayor|superior)/.test(word)
        ? 'gt'
        : /^(?:menor|inferior)/.test(word)
          ? 'lt'
          : 'eq';
      const value = Number(measureFilter[3]);
      builder.push(
        {
          id: builder.nextId(),
          type: 'addFilter',
          payload: {
            filter: {
              id: builder.nextFilterId(),
              target:
                field.kind === 'measure'
                  ? { kind: 'measure', measureId: field.id }
                  : { kind: 'dimension', dimensionId: field.id },
              operator,
              values: [value],
            },
          },
        },
        `filtrar ${field.label} ${operator} ${String(value)}`,
      );
      return;
    }

    const grouped = new Map<string, (string | number | boolean)[]>();
    for (const valuePhrase of phrase
      .split(/\s*,\s*|\s+y\s+|\s+o\s+|\s+e\s+/)
      .map((v) => v.trim())
      .filter(Boolean)) {
      const resolved = resolveMember(valuePhrase);
      const list = grouped.get(resolved.dimensionId) ?? [];
      if (!list.includes(resolved.value)) list.push(resolved.value);
      grouped.set(resolved.dimensionId, list);
    }
    for (const [dimensionId, values] of grouped) {
      const field = matcher.field(dimensionId);
      builder.push(
        {
          id: builder.nextId(),
          type: 'addFilter',
          payload: {
            filter: {
              id: builder.nextFilterId(),
              target: { kind: 'dimension', dimensionId },
              operator: values.length > 1 ? 'in' : 'eq',
              values,
            },
          },
        },
        `filtrar ${field?.label ?? dimensionId} = ${values.map(String).join(', ')}`,
      );
    }
  }

  function resolveMember(valuePhrase: string): {
    dimensionId: string;
    value: string | number | boolean;
  } {
    const direct = matcher.matchMember(valuePhrase);
    const tokens = valuePhrase.split(' ');
    const qualified: { dimensionId: string; value: string | number | boolean }[] = [];
    if (direct.length !== 1) {
      // "pais mexico": el prefijo nombra la dimension y el resto es el valor.
      for (let cut = 1; cut < tokens.length; cut++) {
        const outcome = matcher.match(tokens.slice(0, cut).join(' '), 'dimension');
        if (outcome.status !== 'matched') continue;
        qualified.push(...matcher.matchMember(tokens.slice(cut).join(' '), outcome.field.id));
      }
    }
    const matches = qualified.length > 0 ? qualified : direct;
    const [only] = matches;
    if (only && matches.length === 1) return only;
    if (matches.length > 1) {
      return clarify({
        question: `En que campo desea filtrar "${valuePhrase}"?`,
        candidates: matches.map((match) => ({
          id: match.dimensionId,
          label: matcher.field(match.dimensionId)?.label ?? match.dimensionId,
          kind: 'member',
          dimensionId: match.dimensionId,
        })),
        reason: 'El valor existe en varias dimensiones.',
        affectedActionIds: [builder.nextId()],
        term: valuePhrase,
      });
    }
    return fail(
      actionError(
        'VALUE_NOT_FOUND',
        `No encontre "${valuePhrase}" entre los valores conocidos del modelo.`,
        {
          type: 'addFilter',
          term: valuePhrase,
        },
      ),
    );
  }

  if (visualization) {
    builder.push(
      { id: builder.nextId(), type: 'setVisualization', payload: { type: visualization } },
      `cambiar a ${context.visualizations.find((viz) => viz.type === visualization)?.label ?? visualization}`,
    );
  }

  if (sortMatch) {
    const phrase = trimPhrase(sortMatch[1] ?? '');
    const field = resolve(phrase, 'measure');
    const explicit = sortMatch[2];
    const direction: 'asc' | 'desc' =
      explicit === undefined
        ? field.kind === 'measure'
          ? 'desc'
          : 'asc'
        : /^(?:desc|descendente|de mayor a menor)$/.test(explicit)
          ? 'desc'
          : 'asc';
    if (!inAnalysis(field.id)) addField(field);
    builder.push(
      {
        id: builder.nextId(),
        type: 'sort',
        payload: {
          by: [
            {
              target:
                field.kind === 'measure'
                  ? { kind: 'measure', measureId: field.id }
                  : { kind: 'dimension', dimensionId: field.id },
              direction,
            },
          ],
        },
      },
      `ordenar por ${field.label} (${direction})`,
    );
  }

  if (topN) {
    const measureId = addedMeasures[0] ?? analysis?.measures[0]?.measureId;
    if (measureId !== undefined && !sortMatch) {
      builder.push(
        {
          id: builder.nextId(),
          type: 'sort',
          payload: { by: [{ target: { kind: 'measure', measureId }, direction: topN.direction }] },
        },
        `ordenar por ${matcher.field(measureId)?.label ?? measureId}`,
      );
    }
    limit = topN.limit;
  }
  if (limit !== null) {
    builder.push(
      { id: builder.nextId(), type: 'setLimit', payload: { limit } },
      `limitar a ${String(limit)} filas`,
    );
  }
  if (grouping) {
    builder.push(
      { id: builder.nextId(), type: 'updateGrouping', payload: grouping },
      'ajustar totales',
    );
  }

  const recognizedSomething =
    vizMatch !== null ||
    top !== null ||
    limitMatch !== null ||
    sortMatch !== null ||
    grouping !== null ||
    filterMatch !== null ||
    trimPhrase(text) !== '';
  return done(
    recognizedSomething && builder.actions.length === 0
      ? 'Los campos indicados ya forman parte del analisis.'
      : undefined,
  );
}

function pushRemove(builder: ActionBuilder, field: AIContextField): void {
  builder.push(
    field.kind === 'measure'
      ? { id: builder.nextId(), type: 'removeMeasure', payload: { measureId: field.id } }
      : { id: builder.nextId(), type: 'removeDimension', payload: { dimensionId: field.id } },
    `quitar ${field.label}`,
  );
}

const GRAIN_LABELS: Readonly<Record<TimeGrain, string>> = {
  hour: 'hora',
  day: 'dia',
  week: 'semana',
  month: 'mes',
  quarter: 'trimestre',
  year: 'ano',
};
const grainLabel = (grain: TimeGrain): string => GRAIN_LABELS[grain];
